import { useEffect, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { setMuted, isMuted } from "@/lib/sounds";
import { applyKarnoffelMuteFlag } from "@/lib/karnoffelMusic";
import { applyChantMuteFlag } from "@/lib/chantMusic";

/**
 * Persistent global mute toggle. Rendered once at the App root so it
 * appears on every page in the same screen position. The button is
 * marked data-silent so it never plays a click sound when toggled.
 *
 * Styled to read consistently on every page theme (dark backgrounds
 * dominate the site). Z-index sits above TopBar (z-50) so it remains
 * accessible on Tavern / chess / pong pages too.
 */
const GlobalMuteButton = () => {
  const [muted, setMutedState] = useState(isMuted());

  // Persist mute state across reloads via localStorage so refreshing
  // the page doesn't unexpectedly turn audio back on.
  useEffect(() => {
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        const stored = window.localStorage.getItem("studio:muted");
        if (stored === "1") {
          setMuted(true);
          setMutedState(true);
        }
      }
    } catch {}
  }, []);

  const toggle = () => {
    const next = !muted;
    setMuted(next);
    setMutedState(next);
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        window.localStorage.setItem("studio:muted", next ? "1" : "0");
      }
    } catch {}
    // Inform any per-game audio loops so they pause/resume in lock-step.
    applyKarnoffelMuteFlag();
    applyChantMuteFlag();
  };

  return (
    <button
      onClick={toggle}
      data-silent="true"
      aria-label={muted ? "Unmute" : "Mute"}
      title={muted ? "Unmute" : "Mute"}
      data-testid="mute-toggle-button"
      style={{
        position: "fixed",
        top: 10,
        right: 12,
        zIndex: 100,
        padding: "6px 8px",
        borderRadius: 2,
        border: "1px solid rgba(229,200,112,0.45)",
        background: "rgba(12,10,8,0.72)",
        color: "#E5C870",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        cursor: "pointer",
        lineHeight: 0,
      }}
    >
      {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
    </button>
  );
};

export default GlobalMuteButton;
